"use client"

import { t } from "@/languages/i18n"
import { CalendarOutlined, EyeOutlined, KeyOutlined, TranslationOutlined } from "@ant-design/icons"
import { Button, Input, Select, Typography } from "antd"
import dayjs from "dayjs"
import Image from "next/image"
import { useState } from "react"
import { Images } from "../../../../constants/images"

const { Text } = Typography

interface StatusPublishProps {
  setStatus: (status: string) => void
  status: string
}

interface DatePublishProps {
  setPublishDate: (date: string) => void
}

interface LanguagePublishProps {
  language: string
  setLanguages: (language: string) => void
  isCreateEdit: boolean
  existingLanguages?: string[]
  /** Site chỉ có tiếng Việt: khoá ô chọn ngôn ngữ ở VI. */
  viOnly?: boolean
}

export const StatusPublish = ({ setStatus, status }: StatusPublishProps) => {
  const [tempStatus, setTempStatus] = useState(status)
  const [isEditing, setIsEditing] = useState(false)

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleCancelClick = () => {
    setIsEditing(false)
    setTempStatus(status)
  }

  const handleChange = (value: any) => {
    setStatus(value.value)
  }

  const handleOkClick = () => {
    setStatus(tempStatus)
    setIsEditing(false)
  }

  return (
    <div>
      <Text className="text-sm">
        <KeyOutlined /> {t("Status")}:
        <Select
          labelInValue
          className="w-fit ml-2"
          onChange={handleChange}
          options={[
            { value: "Draft", label: t("Draft") },
            { value: "Published", label: t("Publish") },
          ]}
          value={{ value: status, label: t(status) }}
        />
      </Text>

      {isEditing && (
        <div className="mt-2">
          <Select
            labelInValue
            defaultValue={{ value: status }}
            className="w-[100px]"
            onChange={handleChange}
            options={[
              { value: "Draft", label: t("Draft") },
              { value: "Published", label: t("Publish") },
            ]}
          />
          <Button className="ml-2" type="primary" onClick={handleOkClick}>
            {t("Ok")}
          </Button>
          <span className="text-blue-500 underline cursor-pointer ml-2" onClick={handleCancelClick}>
            {t("Cancel")}
          </span>
        </div>
      )}
    </div>
  )
}

export const LanguagePublish = ({
  language,
  setLanguages,
  isCreateEdit,
  existingLanguages = [],
  viOnly = false,
}: LanguagePublishProps) => {
  const [tempStatus, setTempStatus] = useState(language)
  const [isEditing, setIsEditing] = useState(false)

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleCancelClick = () => {
    setIsEditing(false)
    setTempStatus(language)
  }

  const handleChange = (value: any) => {
    setLanguages(value.value)
  }

  const handleOkClick = () => {
    setLanguages(tempStatus)
    setIsEditing(false)
  }

  const languageOptions = [
    {
      value: "vi",
      disabled: existingLanguages.includes("vi"),
      label: (
        <div className="flex items-center gap-2">
          <Image src={Images.logoVi} alt="Vietnamese" className="w-5 h-5" />
          {t("VI")}
        </div>
      ),
    },
    {
      value: "en",
      disabled: existingLanguages.includes("en"),
      label: (
        <div className="flex items-center gap-2">
          <Image src={Images.logoEn} alt="English" className="w-5 h-5" />
          {t("EN")}
        </div>
      ),
    },
    {
      value: "cs",
      disabled: existingLanguages.includes("cs"),
      label: (
        <div className="flex items-center gap-2">
          <Image src={Images.logoCs} alt="Czech" className="w-5 h-5" />
          {t("CS")}
        </div>
      ),
    },
  ]

  const selectedOption = languageOptions.find(option => option.value === language)

  return (
    <div>
      <Text className="text-sm space-x-3">
        <TranslationOutlined /> {t("Language")}:
        <Select
          labelInValue
          className="w-fit"
          onChange={handleChange}
          options={viOnly ? languageOptions.filter(option => option.value === "vi") : languageOptions}
          value={selectedOption ? { value: selectedOption.value, label: selectedOption.label } : undefined}
          disabled={!isCreateEdit || viOnly}
          loading={!language}
        />
      </Text>
    </div>
  )
}

export const VisibilityPublish = ({ status, onView }: { status?: string; onView?: () => void }) => {
  const isPublished = status === "Published"

  return (
    <div>
      <Text className="text-sm">
        <EyeOutlined /> {t("Visibility")}:
        <span className="ml-2">{isPublished ? t("Public") : t("Draft")}</span>
        {isPublished && (
          <span className="text-blue-500 underline cursor-pointer ml-2" onClick={onView}>
            {t("View")}
          </span>
        )}
      </Text>
    </div>
  )
}

export const DatePublish = ({ setPublishDate }: DatePublishProps) => {
  const now = dayjs()
  const [isEditing, setIsEditing] = useState(false)
  const [isPublished, setIsPublished] = useState(false)
  const [publishedDate, setPublishedDate] = useState<string | null>(null)
  const [tempDate, setTempDate] = useState({
    month: now.format("MMM"),
    day: now.format("DD"),
    year: now.format("YYYY"),
    hour: now.format("HH"),
    minute: now.format("mm"),
  })

  const handleEditClick = () => setIsEditing(true)
  const handleCancelClick = () => setIsEditing(false)

  const handleDateChange = (key: string, value: string) => {
    setTempDate(prev => ({ ...prev, [key]: value }))
    setPublishDate(value)
  }

  const handleOkClick = () => {
    const formattedDate = `${tempDate.month} ${tempDate.day}, ${tempDate.year} at ${tempDate.hour}:${tempDate.minute}`
    setPublishedDate(formattedDate)
    setIsEditing(false)
    setIsPublished(true)
  }

  return (
    <div>
      <Text className="text-sm ">
        <CalendarOutlined /> {t("Publish")}:
        <span className="ml-2 text-xs">
          {isPublished
            ? publishedDate
            : new Date().toLocaleString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })}
        </span>
        {!isEditing && (
          <span className="text-blue-500 underline cursor-pointer ml-2" onClick={handleEditClick}>
            {t("Edit")}
          </span>
        )}
      </Text>
      {isEditing && (
        <div className="mt-2 flex flex-col">
          <div className="flex items-center w-[300px]">
            <Select
              defaultValue={tempDate.month}
              className="w-[100px]"
              onChange={value => handleDateChange("month", value)}
              options={[
                { value: "Jan", label: t("01-Jan") },
                { value: "Feb", label: t("02-Feb") },
                { value: "Mar", label: t("03-Mar") },
                { value: "Apr", label: t("04-Apr") },
                { value: "May", label: t("05-May") },
                { value: "Jun", label: t("06-Jun") },
                { value: "Jul", label: t("07-Jul") },
                { value: "Aug", label: t("08-Aug") },
                { value: "Sep", label: t("09-Sep") },
                { value: "Oct", label: t("10-Oct") },
                { value: "Nov", label: t("11-Nov") },
                { value: "Dec", label: t("12-Dec") },
              ]}
            />
            <Input value={tempDate.day} onChange={e => handleDateChange("day", e.target.value)} className="w-[45px]" />
            ,
            <Input
              value={tempDate.year}
              onChange={e => handleDateChange("year", e.target.value)}
              className="w-[60px]"
            />
            at
            <Input
              value={tempDate.hour}
              onChange={e => handleDateChange("hour", e.target.value)}
              className="w-[45px]"
            />
            :
            <Input
              value={tempDate.minute}
              onChange={e => handleDateChange("minute", e.target.value)}
              className="w-[45px]"
            />
          </div>
          <div className="mt-2">
            <Button type="primary" onClick={handleOkClick}>
              {t("Ok")}
            </Button>
            <span className="text-blue-500 underline cursor-pointer ml-2" onClick={handleCancelClick}>
              {t("Cancel")}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}